/**
 * Session Schedule - Simple Mode Component (JSX)
 * Handles session type toggle and time ranges for same-time mode
 */

import React from 'react';
import { Trash2, X } from 'lucide-react';
import { AddPlaceholder } from '../common/AddPlaceHolderV2';
import { createDefaultSession } from '../../utils/sessionMigrations';

// Reusable Session Renderer Component
const SessionRenderer = ({
  session,
  sessionIndex,
  showDeleteButton = false,
  showSessionLabel = false,
  onUpdate,
  onDelete,
}) => {
  return (
    <div className="space-y-2.5">
      {/* Session Header (only for multiple mode with label) */}
      {showSessionLabel && (
        <div className="flex items-center justify-between">
          <input
            type="text"
            value={session.name || ''}
            onChange={(e) => {
              onUpdate({
                ...session,
                name: e.target.value,
              });
            }}
            placeholder={`Session ${sessionIndex + 1}`}
            className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-900 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-[#61b8e7] rounded px-0 py-0"
          />
          {showDeleteButton && onDelete && (
            <button
              type="button"
              onClick={onDelete}
              className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
              title="Remove session"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Primary Range */}
      <div className="flex items-center gap-2">
        <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 w-11 flex-shrink-0">
          From
        </span>
        <input
          type="time"
          value={session.primaryRange.from}
          onChange={(e) => {
            onUpdate({
              ...session,
              primaryRange: {
                ...session.primaryRange,
                from: e.target.value,
              },
            });
          }}
          className="w-[140px] border border-gray-300 rounded-lg px-3 py-2 font-['Roboto:Regular',sans-serif] text-[13px] focus:border-[#61b8e7] focus:outline-none focus:ring-1 focus:ring-[#61b8e7]"
        />
        <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 flex-shrink-0">
          To
        </span>
        <input
          type="time"
          value={session.primaryRange.to}
          onChange={(e) => {
            onUpdate({
              ...session,
              primaryRange: {
                ...session.primaryRange,
                to: e.target.value,
              },
            });
          }}
          className="w-[140px] border border-gray-300 rounded-lg px-3 py-2 font-['Roboto:Regular',sans-serif] text-[13px] focus:border-[#61b8e7] focus:outline-none focus:ring-1 focus:ring-[#61b8e7]"
        />
      </div>

      {/* Alternative Ranges */}
      {(session.alternativeRanges || []).map((altRange, altIndex) => (
        <div key={altIndex} className="flex items-center gap-2">
          <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 w-11 flex-shrink-0">
            From
          </span>
          <input
            type="time"
            value={altRange.from}
            onChange={(e) => {
              const updatedAlternatives = [...(session.alternativeRanges || [])];
              updatedAlternatives[altIndex] = {
                ...updatedAlternatives[altIndex],
                from: e.target.value,
              };
              onUpdate({
                ...session,
                alternativeRanges: updatedAlternatives,
              });
            }}
            className="w-[140px] border border-gray-300 rounded-lg px-3 py-2 font-['Roboto:Regular',sans-serif] text-[13px] focus:border-[#61b8e7] focus:outline-none focus:ring-1 focus:ring-[#61b8e7]"
          />
          <span className="font-['Roboto:Regular',sans-serif] text-[13px] text-gray-600 flex-shrink-0">
            To
          </span>
          <input
            type="time"
            value={altRange.to}
            onChange={(e) => {
              const updatedAlternatives = [...(session.alternativeRanges || [])];
              updatedAlternatives[altIndex] = {
                ...updatedAlternatives[altIndex],
                to: e.target.value,
              };
              onUpdate({
                ...session,
                alternativeRanges: updatedAlternatives,
              });
            }}
            className="w-[140px] border border-gray-300 rounded-lg px-3 py-2 font-['Roboto:Regular',sans-serif] text-[13px] focus:border-[#61b8e7] focus:outline-none focus:ring-1 focus:ring-[#61b8e7]"
          />
          <button
            type="button"
            onClick={() => {
              const updatedAlternatives = (session.alternativeRanges || []).filter(
                (_, i) => i !== altIndex,
              );
              onUpdate({
                ...session,
                alternativeRanges: updatedAlternatives,
              });
            }}
            className="w-8 h-8 flex items-center justify-center rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors flex-shrink-0"
            title="Remove alternative range"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}

      {/* Add Alternative Range */}
      {(session.alternativeRanges || []).length < 2 && (
        <AddPlaceholder
          variant="link"
          label="Add Alternative Range"
          onClick={() => {
            onUpdate({
              ...session,
              alternativeRanges: [
                ...(session.alternativeRanges || []),
                { from: '13:00', to: '17:00' },
              ],
            });
          }}
        />
      )}
    </div>
  );
};

export const SessionScheduleSimple = ({
  sessionType,
  sessions,
  daysCount,
  onSessionTypeChange,
  onSessionsUpdate,
}) => {
  // Calculate total cleanings per week
  const totalCleanings = sessionType === 'single' ? daysCount : daysCount * (sessions?.length || 0);

  return (
    <>
      {/* Session Type Toggle */}
      <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
        <label className="block font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700 mb-3">
          Session Type
        </label>

        <div className="space-y-2">
          {/* Single Session Option */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="radio"
              name="session-type-simple"
              checked={sessionType === 'single'}
              onChange={() => onSessionTypeChange('single')}
              className="mt-0.5 w-4 h-4 text-[#61b8e7] focus:ring-[#61b8e7] focus:ring-offset-0"
            />
            <div className="flex-1">
              <div className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-900">
                Single session per day
              </div>
              <div className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 mt-0.5">
                Clean once, janitor chooses from available time options
              </div>
            </div>
          </label>

          {/* Multiple Sessions Option */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="radio"
              name="session-type-simple"
              checked={sessionType === 'multiple'}
              onChange={() => onSessionTypeChange('multiple')}
              className="mt-0.5 w-4 h-4 text-[#61b8e7] focus:ring-[#61b8e7] focus:ring-offset-0"
            />
            <div className="flex-1">
              <div className="font-['Roboto:Medium',sans-serif] text-[13px] text-gray-900">
                Multiple sessions per day
              </div>
              <div className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-600 mt-0.5">
                Clean multiple times, all sessions required
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Time Schedule */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-700">
            Time Schedule
          </label>
          {daysCount > 0 && (
            <span className="font-['Roboto:Regular',sans-serif] text-[12px] text-gray-500">
              {totalCleanings} cleanings/week
            </span>
          )}
        </div>

        {/* SINGLE SESSION MODE */}
        {sessionType === 'single' && (sessions || []).length > 0 && (
          <SessionRenderer
            session={sessions[0]}
            sessionIndex={0}
            showDeleteButton={false}
            showSessionLabel={false}
            onUpdate={(updatedSession) => {
              onSessionsUpdate([updatedSession]);
            }}
          />
        )}

        {/* MULTIPLE SESSIONS MODE */}
        {sessionType === 'multiple' && (
          <div className="space-y-3">
            {(sessions || []).map((session, sessionIndex) => (
              <div key={session.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                {/* Session Header */}
                <div className="flex items-center justify-between mb-3">
                  <input
                    type="text"
                    value={session.name || ''}
                    onChange={(e) => {
                      const updatedSessions = [...(sessions || [])];
                      updatedSessions[sessionIndex] = {
                        ...updatedSessions[sessionIndex],
                        name: e.target.value,
                      };
                      onSessionsUpdate(updatedSessions);
                    }}
                    placeholder={`Session ${sessionIndex + 1}`}
                    className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-900 bg-transparent border-none focus:outline-none focus:ring-1 focus:ring-[#61b8e7] rounded px-2 py-1"
                  />
                  {(sessions || []).length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const updatedSessions = (sessions || []).filter(
                          (_, i) => i !== sessionIndex,
                        );
                        onSessionsUpdate(updatedSessions);
                      }}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove session"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                {/* Session Content - Same as Single Mode */}
                <SessionRenderer
                  session={session}
                  sessionIndex={sessionIndex}
                  showDeleteButton={false}
                  showSessionLabel={false}
                  onUpdate={(updatedSession) => {
                    const updatedSessions = [...(sessions || [])];
                    updatedSessions[sessionIndex] = updatedSession;
                    onSessionsUpdate(updatedSessions);
                  }}
                />
              </div>
            ))}

            {/* Add Session Button */}
            {(sessions || []).length < 5 && (
              <AddPlaceholder
                variant="link"
                label="Add Session"
                onClick={() => {
                  onSessionsUpdate([...(sessions || []), createDefaultSession()]);
                }}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
};
